import { listAllStns, type Stn, type StnListParams } from '@api/warehouse-ops';
import { DataSheet } from '@components/ui/DataSheet';
import StatCard from '@components/ui/StatCard';
import { Group, Text } from '@mantine/core';
import { X } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo } from 'react';

import { buildStnColumns, formatUnits } from './stnColumns';
import { OPEN_STN_STATUSES, SOURCE_LABEL } from './stnLabels';

type Props = {
    itemId: string;
    itemName: string | null;
    /** The sheet's own narrowing, so the panel lists exactly the notes the row counted. */
    filters: Pick<StnListParams, 'sources' | 'fromNodeIds' | 'toNodeIds' | 'search'>;
    onClose: () => void;
    onOpenNote: (stnId: string) => void;
};

/** Every row is this SKU and the origin sits in the header line, so these would only repeat. */
const DROPPED_COLUMNS = new Set(['fromNodeId', 'itemType', 'workOrderId']);

/**
 * Units of this SKU still to land on one note. With the item filter on the list, a note's
 * totals are this SKU's line, so the Notes sheet's three figures give the answer directly.
 */
const openUnits = (row: Stn): number =>
    Math.max((row.totalDispatchedQty ?? row.totalPlannedQty ?? 0) - (row.totalReceivedQty ?? 0), 0);

/**
 * The By SKU sheet's blow-up: the open notes carrying one SKU, one row per note with that
 * note's line quantities for the SKU. Same columns as the Notes sheet, same list read with
 * the item filter, so the two never disagree.
 */
const StnSkuPanel: FC<Props> = ({ itemId, itemName, filters, onClose, onOpenNote }) => {
    // The list pages at 50 and a SKU can sit on more open notes than that (HealthFab raises
    // one Amazon shipment per FC), so take every page: the totals must match the sheet row.
    const query = useQuery({
        queryKey: ['stns-for-sku', itemId, filters],
        queryFn: () => listAllStns({ itemIds: [itemId], statuses: OPEN_STN_STATUSES, ...filters }),
    });
    // A note Amazon is still receiving on other sizes is open at note level but done for this
    // SKU once its line is fully in; the sheet row counts the same way, so the two agree.
    const rows = useMemo(() => (query.data ?? []).filter((row) => openUnits(row) > 0), [query.data]);
    const columns = useMemo(() => buildStnColumns().filter((column) => !DROPPED_COLUMNS.has(column.key)), []);

    const summary = useMemo(() => {
        const bySource = new Map<string, number>();
        let planned = 0;
        let dispatched: number | null = null;
        let open = 0;
        for (const row of rows) {
            const source = SOURCE_LABEL[row.source ?? ''] ?? row.source ?? 'Other';
            bySource.set(source, (bySource.get(source) ?? 0) + 1);
            planned += row.totalPlannedQty ?? 0;
            if (row.totalDispatchedQty !== null && row.totalDispatchedQty !== undefined) {
                dispatched = (dispatched ?? 0) + row.totalDispatchedQty;
            }
            open += openUnits(row);
        }
        const mix = [...bySource.entries()].map(([label, n]) => `${n} ${label}`).join(', ');
        return { planned, dispatched, open, mix };
    }, [rows]);

    const noteWord = rows.length === 1 ? 'open note' : 'open notes';

    return (
        <div className="fixed inset-0 z-30 flex items-stretch justify-end bg-slate-900/10 backdrop-blur-sm">
            <div className="relative flex h-full w-full max-w-4xl flex-col border-l border-slate-200 bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
                    <div>
                        <Text size="lg" fw={700}>{itemName ?? itemId}</Text>
                        <Text size="xs" c="dimmed" mt={2}>
                            {itemId}
                            {query.data && ` · ${rows.length} ${noteWord}`}
                        </Text>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 text-slate-500 hover:bg-slate-100" aria-label="Close">
                        <X size={18} />
                    </button>
                </div>

                {/* The same summary cards the other tabs open with. */}
                <Group gap={16} grow align="stretch" px="lg" py="md">
                    <StatCard label="Open notes" value={rows.length} helper={summary.mix || undefined} loading={query.isLoading} />
                    <StatCard label="Units planned" value={formatUnits(summary.planned)} loading={query.isLoading} />
                    <StatCard label="Units dispatched" value={formatUnits(summary.dispatched)} helper="left the source" loading={query.isLoading} />
                    <StatCard label="Open" value={formatUnits(summary.open)} helper="still to land" loading={query.isLoading} />
                </Group>

                <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-6 py-5">
                    <DataSheet<Stn>
                        title="Open notes"
                        data={rows}
                        columns={columns}
                        isLoading={query.isLoading}
                        getRowKey={(row) => row.id}
                        onRowClick={(row) => onOpenNote(row.id)}
                        enableColumnPicker
                        columnPickerKey="stn_sku_panel_cols_v1"
                        enableDownload
                        downloadFilename={`${itemId}-still-to-land.csv`}
                        maxHeight="auto"
                        emptyState={
                            <Text size="13px" c="dimmed" ta="center" p={32}>
                                Nothing of this SKU is still to land under the current filters.
                            </Text>
                        }
                    />
                </div>
            </div>
        </div>
    );
};

export default StnSkuPanel;
